import { useCallback, useEffect, useRef, useState } from "react";
import {
  conectarSocketLote,
  obterStatusLote,
} from "../../../servicos/emplacarLotes";

const POLLING_INTERVAL = 5000;
const TERMINAL_JOB_STATUSES = new Set([
  "success",
  "sucesso",
  "completed",
  "done",
  "finalizado",
  "failed",
  "falha",
  "erro",
  "error",
  "canceled",
  "cancelado",
]);

const normalizeStatusValue = (value) =>
  typeof value === "string" ? value.toLowerCase() : "";

const parseCount = (value) => {
  const numero = Number(value);
  return Number.isFinite(numero) ? numero : 0;
};

const isJobCompleted = (payload = {}) => {
  const statusLower = normalizeStatusValue(payload.status || payload.state);
  if (TERMINAL_JOB_STATUSES.has(statusLower)) {
    return true;
  }
  const total = parseCount(payload.total ?? payload.totalItems ?? payload.count);
  if (total <= 0) {
    return false;
  }
  const processed = parseCount(payload.processed ?? payload.done ?? payload.completed);
  if (processed >= total && processed > 0) {
    return true;
  }
  const success = parseCount(payload.success ?? payload.ok);
  const failed = parseCount(payload.failed ?? payload.errors);
  const concluido = success + failed;
  return concluido >= total && concluido > 0;
};

export const normalizeJobUpdate = (payload) => ({
  status: payload?.status || "pending",
  processed: payload?.processed ?? payload?.done ?? 0,
  success: payload?.success ?? payload?.ok ?? 0,
  failed: payload?.failed ?? payload?.errors ?? 0,
  total: payload?.total ?? payload?.totalItems ?? payload?.count ?? 0,
  updatedAt: payload?.updatedAt || payload?.timestamp || new Date().toISOString(),
  raw: payload,
});

export const useLoteJobSubscription = ({
  onJobUpdate,
  onJobItem,
  onFallbackTick,
  fetchStatus,
} = {}) => {
  const [jobKey, setJobKey] = useState(null);
  const [connected, setConnected] = useState(false);
  const [fallbackActive, setFallbackActive] = useState(false);
  const socketRef = useRef(null);
  const pollingRef = useRef(null);
  const jobCompletedRef = useRef(false);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    setFallbackActive(false);
  }, []);

  const disconnectSocket = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    setConnected(false);
  }, []);

  const cleanup = useCallback(() => {
    stopPolling();
    disconnectSocket();
    setJobKey(null);
  }, [disconnectSocket, stopPolling]);

  useEffect(() => cleanup, [cleanup]);

  const finalizeMonitoring = useCallback(() => {
    if (jobCompletedRef.current) {
      return;
    }
    jobCompletedRef.current = true;
    cleanup();
  }, [cleanup]);

  const startFallback = useCallback(
    (key) => {
      if (!key) {
        return;
      }
      if (pollingRef.current) {
        return;
      }
      if (jobCompletedRef.current) {
        return;
      }
      setFallbackActive(true);
      const fetcher = fetchStatus || obterStatusLote;
      pollingRef.current = setInterval(async () => {
        try {
          const data = await fetcher(key);
          const jobPayload = data?.job || data || {};
          if (onJobUpdate) {
            onJobUpdate(normalizeJobUpdate(jobPayload));
          }
          const itensFallback = (() => {
            if (Array.isArray(data?.items)) {
              return data.items;
            }
            if (Array.isArray(data?.itens)) {
              return data.itens;
            }
            if (Array.isArray(jobPayload?.items)) {
              return jobPayload.items;
            }
            if (Array.isArray(jobPayload?.itens)) {
              return jobPayload.itens;
            }
            return [];
          })();
          if (onJobItem) {
            itensFallback.forEach((item) => onJobItem(item));
          }
          if (onFallbackTick) {
            onFallbackTick(data);
          }
          if (isJobCompleted(jobPayload)) {
            finalizeMonitoring();
          }
        } catch (error) {
          // keep polling; we only surface via optional callback
        }
      }, POLLING_INTERVAL);
    },
    [fetchStatus, finalizeMonitoring, onFallbackTick, onJobItem, onJobUpdate],
  );

  const attachSocket = useCallback(
    (key) => {
      const socket = conectarSocketLote();
      socketRef.current = socket;

      socket.on("connect", () => {
        stopPolling();
        setConnected(true);
        socket.emit("subscribeJob", { jobKey: key });
      });

      socket.on("disconnect", () => {
        if (jobCompletedRef.current) {
          return;
        }
        setConnected(false);
        startFallback(key);
      });

      socket.on("connect_error", () => {
        if (jobCompletedRef.current) {
          return;
        }
        setConnected(false);
        startFallback(key);
      });

      socket.on("job:update", (payload) => {
        if (onJobUpdate) {
          onJobUpdate(normalizeJobUpdate(payload));
        }
      });

      socket.on("job:item", (payload) => {
        if (onJobItem) {
          onJobItem(payload);
        }
      });

      socket.on("job:complete", (payload) => {
        if (onJobUpdate) {
          onJobUpdate(normalizeJobUpdate(payload));
        }
        finalizeMonitoring();
      });
    },
    [finalizeMonitoring, onJobItem, onJobUpdate, startFallback, stopPolling],
  );

  const start = useCallback(
    (key) => {
      if (!key) {
        return;
      }
      jobCompletedRef.current = false;
      setJobKey(key);
      attachSocket(key);
    },
    [attachSocket],
  );

  const stop = useCallback(() => {
    cleanup();
  }, [cleanup]);

  return {
    start,
    stop,
    jobKey,
    connected,
    fallbackActive,
  };
};
